/**
 * Change one column on many Containers at once (Alan, 2026-10-11: "我需要这个系统表格就是可以多选柜子然后
 * 可以批量的改状态...批量的选择一些柜子后可以批量的更改那个column的信息").
 *
 * Tick rows in the table's select mode, pick a column, give it a value, apply. Nothing new is allowed
 * here: every write goes through the same save a typed cell uses - the Dashboard autosave controller, one
 * Container at a time - so each one keeps its own permission check, revision and audit line, and a row the
 * role may not edit (a finished Container, a customer's own chassis) is skipped and named, never forced.
 *
 * Left out on purpose: the identity and the pairing (Container #, Empty #), the customer (moving one is its
 * own careful flow), Report Empty and Actual Return (their own editors), and Weight (its own unit switch).
 */

import { columnEditableForRole, fieldOptions } from './container-field-registry.mjs?v=20260806.100';
import { dashboardEditorValue } from './dashboard-cell-interactions.mjs?v=20260806.100';
import { typedDriverName } from './dashboard-driver-dropdown.mjs?v=20260806.100';
import { dashboardAutosaveErrorCode } from './dashboard-field-autosave.mjs?v=20260806.100';
import { columnEditableForRow } from './adaptive-container-table.mjs?v=20260806.100';
import { TYPED_DATE_TIME_FIELDS, TYPED_DATE_TIME_PLACEHOLDER } from './typed-date.mjs?v=20260806.100';

const BULK_EDITORS = new Set(['text', 'select', 'date', 'number', 'currency', 'note', 'driver']);
const BULK_EXCLUDED_KEYS = new Set([
  'container_no', 'empty_container_no', 'customer_name', 'reported_empty_at', 'actual_empty_returned_at', 'weight_lbs',
]);

function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;').replaceAll("'", '&#039;');
}

// CTF and TMF are one chip pair in the table (ctf_tmf_status, read-only); each fee is edited on its own, so each
// is its own bulk column (Alan, 2026-10-11: "批量修改的种类包含LFD, CTF, TMF, 和hold"). Finance and Admin only,
// like the chip. Customer Paid is a third answer to a fee's own question, as in the chip's menu.
const BULK_FEE_COLUMNS = Object.freeze(['ctf_status', 'tmf_status'].map((key) => Object.freeze({
  key,
  label: key === 'ctf_status' ? 'CTF' : 'TMF',
  editor: 'select',
  editRoles: Object.freeze(['finance']),
  options: Object.freeze(['paid', 'unpaid', 'customer_paid']),
})));
const FEE_OPTION_LABELS = Object.freeze({ paid: 'Paid', unpaid: 'Unpaid', customer_paid: 'Customer Paid' });

/** The columns this role may change in bulk: what it may edit in a cell, less the flows named above, plus the fees. */
export function bulkEditColumns(columns, role) {
  const fromTable = (Array.isArray(columns) ? columns : []).filter((column) => (
    column && BULK_EDITORS.has(column.editor) && !BULK_EXCLUDED_KEYS.has(column.key) && !column.detailOnly
    && columnEditableForRole(column, role)
  ));
  return [...fromTable, ...BULK_FEE_COLUMNS.filter((column) => columnEditableForRole(column, role))];
}

/** What the value box for a column is: a menu, a menu that also takes typing, a name, a date or plain text. */
export function bulkEditControlSpec(column) {
  if (column.key === 'ctf_status' || column.key === 'tmf_status') {
    return { kind: 'select', options: [...column.options], labels: FEE_OPTION_LABELS };
  }
  if (column.editor === 'select') {
    const options = fieldOptions(column).filter((option) => option !== 'CUSTOM');
    return { kind: column.allowCustom ? 'combo' : 'select', options };
  }
  if (column.editor === 'driver') return { kind: 'driver' };
  if (column.editor === 'date') {
    if (TYPED_DATE_TIME_FIELDS.has(column.key)) return { kind: 'text', placeholder: TYPED_DATE_TIME_PLACEHOLDER };
    return { kind: 'date', inputType: column.format === 'date-only' ? 'date' : 'datetime-local' };
  }
  return { kind: 'text', placeholder: '' };
}

/**
 * The value to save for what was entered, or throws why not. A blank clears the column - the same
 * meaning an emptied cell has - and is only offered when `clear` was asked for, so an untouched box is
 * never read as "erase all of these".
 */
export function bulkEditValue(column, raw, { clear = false, drivers = [] } = {}) {
  if (clear) return dashboardEditorValue(column, '');
  const text = String(raw ?? '').trim();
  if (!text) throw new TypeError('EMPTY');
  if (column.editor === 'driver') return typedDriverName(text, drivers);
  return dashboardEditorValue(column, text);
}

/** Every selected row, and whether this role may change this column on it. */
export function planBulkEdit({ column, rows, role }) {
  return (Array.isArray(rows) ? rows : []).map((row) => ({
    id: row.id,
    containerNo: String(row.container_no ?? row.id ?? ''),
    status: columnEditableForRow(column, role, row) ? 'ready' : 'locked',
  }));
}

const FAILURE_TEXT = Object.freeze({
  CONTAINER_COMPLETED: '柜子已完成',
  CONTAINER_BILLED: '已开票',
  PERMISSION_DENIED: '无权限',
  CUSTOMER_WINDOW_CLOSED: '客户已不能改',
  REVISION_CONFLICT: '刚被别人改过',
  VALUE_INVALID: '值不对',
  DASHBOARD_CUSTOMER_OWN_CHASSIS: '客户自带车架',
});

export function bulkEditFailureText(error) {
  const code = dashboardAutosaveErrorCode(error);
  return FAILURE_TEXT[code] ?? (String(error?.message || code).slice(0, 60));
}

/**
 * Save the value on every ready entry, a few at a time. Resolves with the tally; it never throws for one
 * row's failure - that row is named and the rest carry on.
 */
export async function runBulkEdit({
  entries, field, value, save, concurrency = 3, onProgress = () => {},
}) {
  const ready = entries.filter((entry) => entry.status === 'ready');
  const tally = {
    saved: 0, unchanged: 0, locked: entries.length - ready.length, failed: [],
  };
  let next = 0;
  const worker = async () => {
    while (next < ready.length) {
      const entry = ready[next];
      next += 1;
      try {
        const result = await save({ containerId: entry.id, field, value });
        if (result?.unchanged) tally.unchanged += 1;
        else tally.saved += 1;
      } catch (error) {
        if (dashboardAutosaveErrorCode(error) === 'NO_CHANGE') tally.unchanged += 1;
        else tally.failed.push({ containerNo: entry.containerNo, reason: bulkEditFailureText(error) });
      }
      onProgress({ done: tally.saved + tally.unchanged + tally.failed.length, total: ready.length });
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, ready.length) }, worker));
  return tally;
}

/** One sentence for the result: what changed, what was already so, what was skipped, what failed and why. */
export function bulkEditSummary(tally) {
  const parts = [`已改 ${tally.saved} 个`];
  if (tally.unchanged) parts.push(`${tally.unchanged} 个本来就是这个值`);
  if (tally.locked) parts.push(`${tally.locked} 个不能改，已跳过`);
  if (tally.failed.length) {
    const byReason = new Map();
    tally.failed.forEach(({ reason }) => byReason.set(reason, (byReason.get(reason) ?? 0) + 1));
    parts.push(`${tally.failed.length} 个失败（${[...byReason].map(([reason, count]) => `${reason} ${count}`).join('、')}）`);
  }
  return parts.join(' · ');
}

function valueControlMarkup(column, spec) {
  if (spec.kind === 'select') {
    return `<select data-bulk-value aria-label="新的值"><option value="">选择新的值…</option>${
      spec.options.map((option) => `<option value="${escapeHtml(option)}">${escapeHtml(spec.labels?.[option] ?? option)}</option>`).join('')}</select>`;
  }
  if (spec.kind === 'combo') {
    return `<input data-bulk-value type="text" list="bulkEditOptions" placeholder="选择或输入新的值" aria-label="新的值" autocomplete="off"><datalist id="bulkEditOptions">${
      spec.options.map((option) => `<option value="${escapeHtml(option)}"></option>`).join('')}</datalist>`;
  }
  if (spec.kind === 'driver') {
    return '<input data-bulk-value type="text" list="bulkEditDrivers" placeholder="司机名字" aria-label="新的值" autocomplete="off"><datalist id="bulkEditDrivers"></datalist>';
  }
  if (spec.kind === 'date') {
    return `<input data-bulk-value type="${spec.inputType}" aria-label="新的值">`;
  }
  return `<input data-bulk-value type="text" placeholder="${escapeHtml(spec.placeholder || '新的值')}" aria-label="新的值" autocomplete="off">`;
}

/**
 * The bar above the table. `getTables()` returns the tables to act on (the single one, or both split
 * panes); `save` is the Dashboard's own field save.
 */
export function mountDashboardBulkEditBar({
  root, button, role, columns, getTables, save, loadDriverOptions = async () => [], now = () => Date.now(),
}) {
  if (!root || !button || typeof getTables !== 'function' || typeof save !== 'function') {
    return { destroy() {} };
  }
  const choices = bulkEditColumns(columns, role);
  // A role with nothing it may change in bulk has no use for the button.
  if (!choices.length) {
    button.hidden = true;
    return { destroy() {} };
  }
  let on = false;
  let busy = false;
  let confirming = false;
  let drivers = [];
  let driversAsked = false;

  root.innerHTML = `<span class="bulk-edit-bar__count" data-bulk-count>已选 0</span>
    <button type="button" class="topbar-compact-action" data-bulk-select-all>全选当前</button>
    <button type="button" class="topbar-compact-action" data-bulk-clear>取消选择</button>
    <span class="bulk-edit-bar__sep" aria-hidden="true"></span>
    <label class="bulk-edit-bar__field"><span>把</span><select data-bulk-column aria-label="要改的列"><option value="">选择列…</option>${
      choices.map((column) => `<option value="${escapeHtml(column.key)}">${escapeHtml(column.headerLabel || column.label || column.key)}</option>`).join('')}</select></label>
    <span class="bulk-edit-bar__field"><span>改成</span><span data-bulk-value-host></span></span>
    <label class="bulk-edit-bar__clear" data-bulk-clear-wrap hidden><input type="checkbox" data-bulk-clear-value> 清空</label>
    <button type="button" class="topbar-compact-action bulk-edit-bar__apply" data-bulk-apply disabled>应用</button>
    <button type="button" class="topbar-compact-action" data-bulk-cancel hidden>取消</button>
    <span class="bulk-edit-bar__status" data-bulk-status role="status" aria-live="polite"></span>`;
  const q = (selector) => root.querySelector(selector);
  const columnSelect = q('[data-bulk-column]');
  const valueHost = q('[data-bulk-value-host]');
  const applyButton = q('[data-bulk-apply]');
  const cancelButton = q('[data-bulk-cancel]');
  const clearWrap = q('[data-bulk-clear-wrap]');
  const clearBox = q('[data-bulk-clear-value]');
  const status = q('[data-bulk-status]');
  const countLabel = q('[data-bulk-count]');

  const tables = () => getTables().filter((table) => typeof table?.setSelectMode === 'function');
  const selectedRows = () => tables().flatMap((table) => table.selectedRows());
  const column = () => choices.find((entry) => entry.key === columnSelect.value) ?? null;
  const valueInput = () => valueHost.querySelector('[data-bulk-value]');

  function readValue() {
    const chosen = column();
    if (!chosen) return { error: '先选要改的列' };
    try {
      return { value: bulkEditValue(chosen, valueInput()?.value, { clear: clearBox.checked, drivers }) };
    } catch (error) {
      if (error?.message === 'EMPTY') return { error: '先填新的值，或勾选“清空”' };
      return { error: error?.message || '值不对' };
    }
  }

  function paint() {
    const count = selectedRows().length;
    countLabel.textContent = `已选 ${count}`;
    const ready = on && !busy && count > 0 && column() !== null;
    applyButton.disabled = !ready;
    if (!confirming) applyButton.textContent = '应用';
    cancelButton.hidden = !confirming;
    columnSelect.disabled = busy;
  }

  function stopConfirming() {
    confirming = false;
    paint();
  }

  function setMode(next) {
    on = next;
    button.setAttribute('aria-pressed', String(on));
    root.hidden = !on;
    confirming = false;
    status.textContent = '';
    tables().forEach((table) => table.setSelectMode(on));
    paint();
  }

  columnSelect.addEventListener('change', () => {
    const chosen = column();
    confirming = false;
    status.textContent = '';
    clearBox.checked = false;
    if (!chosen) {
      valueHost.innerHTML = '';
      clearWrap.hidden = true;
      paint();
      return;
    }
    const spec = bulkEditControlSpec(chosen);
    valueHost.innerHTML = valueControlMarkup(chosen, spec);
    // A closed menu has no blank to choose, so clearing stays out of it; every other column can be emptied.
    clearWrap.hidden = spec.kind === 'select';
    if (spec.kind === 'driver' && !driversAsked) {
      driversAsked = true;
      Promise.resolve(loadDriverOptions()).then((list) => {
        drivers = Array.isArray(list) ? list : [];
        const datalist = valueHost.querySelector('#bulkEditDrivers');
        if (datalist) datalist.innerHTML = drivers.map((driver) => `<option value="${escapeHtml(driver.fullName)}"></option>`).join('');
      }).catch(() => {});
    }
    paint();
  });
  valueHost.addEventListener('input', () => { stopConfirming(); status.textContent = ''; });
  clearBox.addEventListener('change', () => { stopConfirming(); });

  button.addEventListener('click', () => setMode(!on));
  q('[data-bulk-select-all]').addEventListener('click', () => {
    tables().forEach((table) => table.selectAllVisible());
    paint();
  });
  q('[data-bulk-clear]').addEventListener('click', () => {
    tables().forEach((table) => table.clearSelection());
    stopConfirming();
  });
  cancelButton.addEventListener('click', () => { status.textContent = ''; stopConfirming(); });
  // The tables say when ticks change; the bar follows.
  const onSelection = () => { if (on) { if (confirming) confirming = false; paint(); } };
  const doc = root.ownerDocument;
  doc.addEventListener('adaptive-selection', onSelection);

  // Two presses, on purpose: this writes to every ticked Container.
  applyButton.addEventListener('click', async () => {
    const chosen = column();
    if (!chosen || busy) return;
    const rows = selectedRows();
    const read = readValue();
    if (read.error) {
      status.textContent = read.error;
      return;
    }
    const entries = planBulkEdit({ column: chosen, rows, role });
    if (!confirming) {
      confirming = true;
      applyButton.textContent = `确认：改 ${entries.length} 个`;
      cancelButton.hidden = false;
      const shown = clearBox.checked ? '（清空）' : `「${String(valueInput()?.value ?? '').trim()}」`;
      status.textContent = `把 ${entries.length} 个柜子的「${chosen.headerLabel || chosen.label}」改成 ${shown}？再点一次确认`;
      return;
    }
    confirming = false;
    busy = true;
    paint();
    applyButton.textContent = '应用中…';
    const tally = await runBulkEdit({
      entries,
      field: chosen.key,
      value: read.value,
      save,
      onProgress: ({ done, total }) => { status.textContent = `应用中 ${done} / ${total}`; },
    });
    busy = false;
    status.textContent = bulkEditSummary(tally);
    if (tally.failed.length) {
      status.title = tally.failed.slice(0, 20).map(({ containerNo, reason }) => `${containerNo}: ${reason}`).join('\n');
    } else {
      status.removeAttribute('title');
    }
    paint();
  });

  root.hidden = true;
  button.setAttribute('aria-pressed', 'false');
  paint();
  return {
    destroy() {
      doc.removeEventListener('adaptive-selection', onSelection);
      if (on) tables().forEach((table) => table.setSelectMode(false));
    },
    isOn: () => on,
    now,
  };
}
